import 'swiper/scss';
import type * as ClassTypes from './types/class';
import {
    Controller,
    EffectCreative,
    EffectFade,
    FreeMode,
    Manipulation,
    Mousewheel,
    Navigation,
    Scrollbar
} from 'swiper/modules';
import {MapPopup, ProgressPopup} from '@/components/popup/popup';
import HorizontalScroll from '@/components/horizontal-scroll/';
import type {IDynamicImport} from './types/utils';
import {initLocationMap} from '@/sections/map/index';
import type {IObserver} from '@/common/scripts/types/observer';
import type {IPopup} from '@/components/popup/types';
import type {IValidation} from '@/components/form/validation/types';
import Observer from './observer';
import ParallaxCircles from '@/components/parallax/';
import Parametric from '@/components/parametric';
import {RESIZE_EVENTS} from '@/common/scripts/constants';
import ScrollSlider from '@/components/scroll-slider/';
import Swiper from 'swiper';
import {Utils} from './utils';
import Validation from '@/components/form/validation';
import VideoPlayer from '@/components/video/';

Swiper.use([Controller, EffectCreative, EffectFade, FreeMode, Manipulation, Mousewheel, Navigation, Scrollbar]);

const observer: IObserver = new Observer();
const validation: IValidation = new Validation();

// Табы
export const initTabs = (elem: HTMLElement): void => {
    import(/* webpackChunkName: "tabs" */ '@/components/tabs')
        .then(({default: Tabs}: IDynamicImport) => {
            new Tabs().init({elem});
        });
};

// Установка размеров видео экрана
export const initFullVideo = (fullIframe: HTMLElement): void => {
    const wrapper = fullIframe.closest('.j-video-iframe__wrapper') as HTMLElement;
    const ratio = 0.5625;
    const setVideoSize = (): void => {
        const width = wrapper ? wrapper.offsetWidth : Utils.getWindowWidth();
        const height = wrapper ? wrapper.offsetHeight : Utils.getWindowHeight();
        const dimension = height / width;

        if (dimension > ratio) {
            fullIframe.style.width = `${height / ratio}px`;
            fullIframe.style.height = `${height + Math.abs(width - (height / ratio))}px`;
            fullIframe.style.top = `${0.5 * (width - (height / ratio))}px`;
            fullIframe.style.left = `${0.5 * (width - (height / ratio))}px`;
        } else {
            fullIframe.style.width = `${width}px`;
            fullIframe.style.height = `${(width * ratio) + (height * ratio)}px`;
            fullIframe.style.top = `${-Math.abs(0.5 * (height * ratio))}px`;
            fullIframe.style.left = '0';
        }
    };

    RESIZE_EVENTS.forEach((event: string) => {
        window.addEventListener(event, setVideoSize);
    });

    setVideoSize();
};

/* Подключение ленивой загрузки (img/picture/iframe) */
export const lazyLoadElements = (lazyElements: Element[]): void => {
    const replaceSource = (element: Element): void => {
        const parentElement = element.parentElement;

        if (!parentElement) {
            return;
        }

        if (parentElement.tagName === 'PICTURE' && element.tagName === 'IMG') {
            element.addEventListener('load', () => {
                parentElement.style.backgroundImage = '';
            });
        }

        const targetAttribute = element.nodeName === 'SOURCE' ? 'srcset' : 'src';
        const sourceElement = element.nodeName === 'SOURCE' ? 'data-srcset' : 'data-src';

        if (element.getAttribute(sourceElement) !== null) {
            element.setAttribute(targetAttribute, element.getAttribute(sourceElement) || '');
            element.removeAttribute(sourceElement);
        }
    };

    if ('IntersectionObserver' in window) {
        // Иначе по событию IntersectionObserver
        const options = {
            rootMargin: '500px'
        };
        const lazyImageObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry: IntersectionObserverEntry) => {
                if (entry.isIntersecting) {
                    const lazyEl = entry.target;

                    replaceSource(lazyEl);
                    lazyImageObserver.unobserve(lazyEl);
                }
            });
        }, options);

        lazyElements.forEach((lazyImage: Element) => {
            if (Utils.isInViewport(lazyImage as HTMLElement,
                window.innerHeight || document.documentElement.clientHeight)) {
                replaceSource(lazyImage);
            } else {
                lazyImageObserver.observe(lazyImage);
            }
        });
    } else {
        // Если есть нативная поддержка атрибута или нет поддержки IntersectionObserver - подменяем сразу
        lazyElements.forEach((lazyEl: Element) => {
            replaceSource(lazyEl);
        });
    }
};

export const initDocs = (element: HTMLElement): void => {
    return new Swiper(element, {
        slideClass   : 'j-documents__list',
        wrapperClass : 'j-documents__wrapper',
        direction    : 'vertical',
        slidesPerView: 'auto',
        scrollbar    : {
            el                    : '.j-scroll',
            dragClass             : 'document__scroll-drag',
            scrollbarDisabledClass: 'is-disabled'
        },
        freeMode  : true,
        mousewheel: {
            releaseOnEdges: true
        }
    });
};

// Контентный слайдер
export const initContentSlider = (element: HTMLElement): void => {
    return new Swiper(element, {
        slideClass   : 'j-content-slider__item',
        wrapperClass : 'j-content-slider__list',
        slidesPerView: 'auto',
        effect       : 'fade',
        speed        : 800,
        autoHeight   : true,
        loop         : true,
        navigation   : {
            prevEl: element.querySelector('.j-arrow-prev'),
            nextEl: element.querySelector('.j-arrow-next')
        }
    });
};

// Слайдер на весь экран
export const initFullSlider = (element: HTMLElement): void => {
    const arrowLeft = element.querySelector('.j-arrow-prev');
    const arrowRight = element.querySelector('.j-arrow-next');

    return new Swiper(element, {
        slideClass    : 'j-slider-full__item',
        wrapperClass  : 'j-slider-full__wrapper',
        slidesPerView : 'auto',
        loop          : true,
        speed         : 800,
        effect        : 'creative',
        creativeEffect: {
            prev: {
                scale    : 1.3,
                translate: ['-20%', 0, 0]
            },
            next: {
                translate: ['100%', 0, 0]
            }
        },
        navigation: {
            prevEl: arrowLeft,
            nextEl: arrowRight
        }
    });
};

// Круглый слайдер
export const initCircleSlider = (element: HTMLElement): void => {
    const arrowLeft = element.querySelector('.j-arrow-prev');
    const arrowRight = element.querySelector('.j-arrow-next');
    const main = element.querySelector('.j-slider-circle__main');
    const second = element.querySelector('.j-slider-circle__second');

    const sliderSecond = new Swiper(second, {
        slideClass         : 'j-slider-circle__second-item',
        wrapperClass       : 'j-slider-circle__second-wrapper',
        slideActiveClass   : 'is-active',
        loop               : true,
        centeredSlides     : true,
        slidesPerView      : 1,
        spaceBetween       : 0,
        speed              : 800,
        watchSlidesProgress: true,
        navigation         : {
            prevEl: arrowLeft,
            nextEl: arrowRight
        }
    });

    sliderSecond.init();

    const sliderMain = new Swiper(main, {
        slideClass         : 'j-slider-circle__main-item',
        wrapperClass       : 'j-slider-circle__main-wrapper',
        slidesPerView      : 1,
        spaceBetween       : 0,
        effect             : 'fade',
        loop               : true,
        watchSlidesProgress: true,
        speed              : 800,
        allowTouchMove     : false
    });

    sliderMain.init();

    sliderSecond.on('slideChangeTransitionStart', () => {
        sliderMain.slideTo(sliderSecond.realIndex);
    });
};


// Горизонтальный слайдер с карточками
export const initHorizontalSlider = (horizontalSlider: HTMLElement): void => {
    return new Swiper(horizontalSlider, {
        wrapperClass     : 'j-horizontal-slider__container',
        slideClass       : 'card',
        slidesPerView    : 'auto',
        touchEventsTarget: 'container',
        scrollbar        : {
            el       : '.j-horizontal-slider__scrollbar',
            draggable: true,
            dragClass: 'horizontal-slider__scroll-drag'
        }
    });
};

// Поведение меню
/* eslint-disable  */
export const initMobileMenu = (burgerMenuSelector: HTMLElement,
                               linksMenuToggle: HTMLElement,
                               menuClose: HTMLElement,
                               menuLinksClose: HTMLElement,
                               headerWrap: HTMLElement): void => {
    import(/* webpackChunkName: "btn-trigger" */ '@/components/button-trigger')
        .then(({default: ButtonTrigger}: IDynamicImport) => {
            const menuTrigger: ClassTypes.IButtonTrigger = new ButtonTrigger();
            const linksTrigger: ClassTypes.IButtonTrigger = new ButtonTrigger();
            const openMenuClass = 'is-open-menu';
            const openLinksClass = 'is-open-links';

            menuTrigger.init({
                target    : burgerMenuSelector,
                eventOpen : 'showMenuTrigger',
                eventClose: 'closeMenuTrigger'
            });

            menuTrigger.init({
                target    : menuClose,
                eventOpen : 'showMenuTrigger',
                eventClose: 'closeMenuTrigger'
            });

            observer.subscribe('showMenuTrigger', () => {
                headerWrap.classList.add(openMenuClass);
                Utils.bodyFixed(headerWrap);
            });

            observer.subscribe('closeMenuTrigger', () => {
                headerWrap.classList.remove(openMenuClass);
                Utils.bodyStatic();
            });

            linksTrigger.init({
                target    : linksMenuToggle,
                eventOpen : 'showLinksTrigger',
                eventClose: 'closeLinksTrigger'
            });

            linksTrigger.init({
                target    : menuLinksClose,
                eventOpen : 'showLinksTrigger',
                eventClose: 'closeLinksTrigger'
            });

            observer.subscribe('showLinksTrigger', () => {
                headerWrap.classList.add(openLinksClass);
                Utils.bodyFixed(headerWrap);
            });

            observer.subscribe('closeLinksTrigger', () => {
                headerWrap.classList.remove(openLinksClass);
                Utils.bodyStatic();
            });

            observer.subscribe('scrollBy:start', () => {
                headerWrap.classList.remove(openMenuClass);
                headerWrap.classList.remove(openLinksClass);
                Utils.bodyStatic();
            });

            RESIZE_EVENTS.forEach((event: string) => {
                window.addEventListener(event, Utils.throttle(() => {
                    menuTrigger.closeByResize();
                }, 250));
            });
        });
};
/* eslint-enable */

// Попапы карточек
export const initContentPopup = (button: HTMLElement): void => {
    const progressPopup = new ProgressPopup({
        openButtons: [button],
        removeDelay: 1000
    });

    progressPopup.init();
};

// Инициализация параллакса
export const initParallax = (wrapper: HTMLElement): void => {
    const parallax = new ParallaxCircles();

    parallax.init({
        wrapper
    });
};

// Инициализация горизонтального скролла
export const initHorizontalScroll = (wrapper: HTMLElement): void => {
    const horizontalScroll = new HorizontalScroll();

    horizontalScroll.init({
        wrapper
    });
};

// Инициализация плеера
export const initVideoPlayer = (player: HTMLElement): void => {
    const videoPlayer = new VideoPlayer();

    videoPlayer.init({
        player
    });
};

// Инициализация scroll slider
export const initScrollSlider = (wrapper: HTMLElement): void => {
    const scrollSlider = new ScrollSlider();

    scrollSlider.init({
        wrapper
    });
};

// Попап с картой инфраструктуры
export const initMapPopup = (button: HTMLElement): void => {
    const mapPopup: IPopup = new MapPopup({
        openButtons: [button],
        removeDelay: 1000,
        modify     : 'popup_theme_map',
        onCreate(): void {
            // @ts-ignore
            initLocationMap(this.popup);
        }
    });

    mapPopup.init();
};

export const initPopup = (buttons: HTMLElement[], otherOptions: Partial<ClassTypes.IPopupFacadeOptions> = {}): void => {
    import(/* webpackChunkName: "popup-facade" */ '@/components/popup/popupFacade')
        .then(({default: PopupFacade}: IDynamicImport) => {
            const options: ClassTypes.IPopupFacadeOptions = {
                ...{openButtons: buttons},
                ...otherOptions
            };

            return new PopupFacade(options).init();
        });
};

// Инициализация формы обратной связи в попапах
export const initCallbackForm = (): void => {
    observer.subscribe('popup:open', (popup: ClassTypes.IPopup) => {
        if (!popup.popup) {
            return;
        }

        // Форма
        const callbackFormElement = popup.popup.querySelector('.j-form') as HTMLElement;

        if (callbackFormElement) {
            validation.update();
            import(/* webpackChunkName: "form" */ '@/components/form')
                .then(({default: Form}: IDynamicImport) => {
                    new Form().init({
                        target        : callbackFormElement,
                        popupInstance : popup,
                        successMessage: true
                    });
                });
        }
    });
};

// Инициализация попапов с формой обратной связи
export const initCallbackPopup = (buttons: HTMLElement[]): void => {
    const options = {
        removeDelay         : 1000,
        closeButtonAriaLabel: 'Закрыть форму обратного звонка',
        modify              : 'popup_theme_callback'
    };

    initCallbackForm();
    initPopup(buttons, options);
};

// Шаг назад
export const stepBack = (back: HTMLElement): void => {
    back.addEventListener('click', (): void => {
        if (history.length === 1) {
            window.location.href = window.location.origin;
        } else {
            history.back();
        }
    });
};

// Шеринг
export const initShare = (shareElements: HTMLElement[]): void => {
    import(/* webpackChunkName: "share" */ '@/components/share')
        .then(({default: Share}: IDynamicImport) => {
            shareElements.forEach((element: HTMLElement) => {
                new Share().init({
                    target: element
                });
            });
        });
};

// Параметрический
/* eslint-disable  */
export const initParametric = (options): void => {
    new Parametric(options).init();
};
/* eslint-enable  */


export const insertPremisesData = (wrapper: HTMLElement, data: ClassTypes.IPopupIframeData): void => {
    const currentForm = wrapper.querySelector('.j-form') as HTMLElement;

    if (!currentForm) {
        return;
    }

    const fillField = currentForm.querySelector('.j-form__fill-field') as HTMLInputElement;

    if (fillField) {
        fillField.value = `${data.description}`;
    }
};
